import { IS_USE_CENTIMETERS_SIZE_COUNTRY } from '@/constants/is-use-centimeters-size-country';
import { CartTriggerFragment } from '@/hooks/Header/cartTriggerFragments.gql';
import { MiniCartFragment } from '@/hooks/MiniCart/miniCartFragments.gql';
import { ADD_SIMPLE_MUTATION } from '@/hooks/ProductFullDetail/productFullDetail.gql';
import { useEnsureCartId } from '@/hooks/use-ensure-cart-id';
import { useResolvePageColumn } from '@/hooks/use-resolve-page-column';
import { useGetFinalPriceAndCrossPrice } from '@/hooks/useGetFinalPriceAndCrossPrice';
import { findMatchingVariant } from '@/util/findMatchingProductVariant';
import { generateAddToCartImage } from '@/util/generate-add-to-cart-image';
import { getBreadcrumbCategoryId } from '@/util/productFullDetailUtil';
import { gql, useMutation } from '@apollo/client';
import { useMemo, useState } from 'react';
import { OptionsSelectionsMap } from '../lib/get-optionSelections';
import { getSelectCustomRule } from '../lib/get-select-custom-rule';
import { useProductFullDetailStore } from '../store/use-product-full-detail-store';
import type { OptionsRoot } from '../types/data';
import { ProductRoot } from '../types/product';

export const ADD_CONFIGURABLE_MUTATION = gql`
  mutation addConfigurableProductToCart(
    $cartId: String!
    $quantity: Float!
    $sku: String!
    $parentSku: String!
    $product_image: String!
    $is_try_on: Int
    $try_on_order_item_id: ID
    $try_on_map_sku: String
    $robes_gifts_customization: [String]
    $color_price: Float
    $in_stock_sku: String
    $weboSku: String
    $category_id: Int
    $category_name: String
  ) {
    addConfigurableProductsToCart(
      input: {
        cart_id: $cartId
        cart_items: [
          {
            data: { quantity: $quantity, sku: $sku }
            parent_sku: $parentSku
            product_image: $product_image
            color_price: $color_price
            is_try_on: $is_try_on
            try_on_order_item_id: $try_on_order_item_id
            try_on_map_sku: $try_on_map_sku
            robes_gifts_customization: $robes_gifts_customization
            in_stock_sku: $in_stock_sku
            weboSku: $weboSku
            category_id: $category_id
            category_name: $category_name
          }
        ]
      }
    ) {
      cart {
        id
        ...CartTriggerFragment
        ...MiniCartFragment
      }
    }
  }
  ${CartTriggerFragment}
  ${MiniCartFragment}
`;

export const useAddToCart = ({
  optionSelections,
  options,
  product,
  mediaGalleryEntries,
  form,
}: {
  product: ProductRoot;
  optionSelections: OptionsSelectionsMap;
  options: OptionsRoot;
  mediaGalleryEntries: any[];
  form: any;
}) => {
  const [addToCartStatus, setAddToCartStatus] = useState(false);

  const [addConfigurableProductToCart, { error: errorAddingConfigurableProduct, loading: isAddConfigurableLoading }] =
    useMutation(ADD_CONFIGURABLE_MUTATION);

  const [addSimpleProductToCart, { error: errorAddingSimpleProduct, loading: isAddSimpleLoading }] =
    useMutation(ADD_SIMPLE_MUTATION);
  const [isCreatingCart, setIsCreatingCart] = useState(false);
  const [showCreateCartError, setShowCreateCartError] = useState(false);

  const { watch } = form;
  const selectedSize = watch('Size');
  const customFieldsVisible = selectedSize?.label === 'customized_size';
  const { finalPrice } = useGetFinalPriceAndCrossPrice({
    product,
    optionSelections,
  });

  const trackAddToCartEvents = (product: ProductRoot, quantity: number) => {
    const currency =
      product?.price_range?.minimum_price?.final_price?.currency ||
      product?.price_range?.maximum_price?.final_price?.currency;
    const price =
      product?.price_range?.minimum_price?.final_price?.value ||
      product?.price_range?.maximum_price?.final_price?.value;

    const breadcrumbCategoryId = getBreadcrumbCategoryId(product?.categories);
    const breadcrumbCategory =
      product?.categories?.find(c => c.uid === breadcrumbCategoryId) ||
      product?.categories?.[product?.categories?.length - 1];
    const categoryName = breadcrumbCategory?.name || '';

    window.dataLayer &&
      window.dataLayer.push({
        ecommerce: null,
      });
    const payload = {
        event: 'add_to_cart',
        ecommerce: {
          currency: currency,
          value: price * quantity,
          items: [
            {
              item_name: product.name,
              item_id: product.sku,
              price: price,
              quantity: quantity,
              item_category: categoryName,
              item_list_name: categoryName,
            },
          ],
        },
      };
    console.log('====== add_to_cart event payload ======', payload);
    window.dataLayer && window.dataLayer.push(payload);

    // Pinterest
    window.pintrk &&
      window.pintrk('track', 'AddToCart', {
        value: price * quantity,
        order_quantity: quantity,
        currency: currency,
      });
  };

  const attributeIdToValuesMap = useMemo(() => {
    const map = new Map();

    // For simple items, this will be an empty map.
    for (const { attribute_id, values } of options) {
      map.set(attribute_id, values);
    }

    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options]);

  const selectedOptionsArray_sp = useMemo(() => {
    const selectedOptions_sp: any = [];

    optionSelections.forEach((value, key) => {
      const values = attributeIdToValuesMap.get(key);

      const selectedValue = values?.find((item: any) => item.value_index === value);

      if (selectedValue) {
        selectedOptions_sp.push({
          id: selectedValue.option_id,
          value_string: selectedValue.value_index,
        });
      }
    });

    return selectedOptions_sp;
  }, [attributeIdToValuesMap, optionSelections]);

  const optionCodes = useProductFullDetailStore(state => state.getOptionCode(product));

  const selectedOptionsArray = useMemo(() => {
    const selectedOptions: any = [];

    optionSelections.forEach((value, key) => {
      const values = attributeIdToValuesMap.get(key);

      const selectedValue = values?.find((item: any) => item.value_index === value);

      if (selectedValue) {
        selectedOptions.push(selectedValue.uid);
      }
    });

    return selectedOptions;
  }, [attributeIdToValuesMap, optionSelections]);

  // 选中颜色后显示当前颜色的价格
  const colorPrice = useMemo(() => {
    const colorOption = product?.ampProtOption?.items?.find(item => item.label.toLowerCase().includes('color'));

    if (!colorOption) {
      return null;
    }

    let selectedColor;

    for (const key of Array.from(optionSelections.keys())) {
      const value = optionSelections.get(key);

      if (key === colorOption.attribute_id) {
        selectedColor = colorOption.values.find(temp => temp.value_index === value);
      }
    }

    if (selectedColor) {
      return selectedColor.price;
    }

    return null;
  }, [optionSelections, product?.ampProtOption?.items]);

  const { resolvePageColumn } = useResolvePageColumn();
  const { handleEnsureCartId } = useEnsureCartId();

  const handleAddToCart = async ({
    allFormData,
    product,
    in_stock_sku,
    showMiniCart = true,
    afterSubmit,
    clearCheckCustomSizeRecord,
    addToCartPayload,
  }: {
    allFormData: any;
    product: ProductRoot;
    in_stock_sku: string;
    showMiniCart?: boolean;
    afterSubmit?: any;
    clearCheckCustomSizeRecord?: () => void;
    addToCartPayload?: Record<any, any>;
  }) => {
    let isSuccess = false;
    const productImage = generateAddToCartImage({ mediaGalleryEntries });
    const { quantity } = allFormData;

    const { robes_gifts_customization } = allFormData;

    const {
      bust,
      waist,
      hip,
      hollowToFloor: hollowtoFloor,
      height,
      extraLength: extralength,
      hollowtoHem,
    } = allFormData || {};

    const { agreement } = allFormData || {};

    const { pocket: pockets, backDetail: back_detail, length } = allFormData || {};

    if (product) {
      try {
        trackAddToCartEvents(product, quantity);
      } catch (error) {
        console.error('跟踪添加到购物车事件时出错:', error);
      }
    }

    let selectCustomRule = [];

    selectCustomRule = getSelectCustomRule(product, allFormData);

    setIsCreatingCart(true);
    setShowCreateCartError(false);
    const nextCartId = await handleEnsureCartId();
    setIsCreatingCart(false);

    if (!nextCartId) {
      setShowCreateCartError(true);
      clearCheckCustomSizeRecord?.();
      return isSuccess;
    }

    const breadcrumbCategoryId = getBreadcrumbCategoryId(product?.categories);
    const breadcrumbCategory = product?.categories?.find(c => c.uid === breadcrumbCategoryId) || product?.categories?.[product?.categories?.length - 1];
    const categoryName = breadcrumbCategory?.name || '';
    
    let categoryId = 0;
    if (breadcrumbCategory?.uid) {
      try {
        const decoded = atob(breadcrumbCategory.uid);
        categoryId = !isNaN(parseInt(decoded, 10)) ? parseInt(decoded, 10) : 0;
      } catch (e) {
        categoryId = parseInt(breadcrumbCategory.uid, 10) || 0;
      }
    }

    try {
      if (product?.__typename === 'SimpleProduct') {
        const variables: any = {
          cartId: nextCartId,
          sku: product.sku,
          quantity,
          customizable_options: selectedOptionsArray_sp,
          bust: bust,
          category_id: categoryId,
          category_name: categoryName,
          customize_price: 0,
          customsize: customFieldsVisible ? 'on' : '',
          data_sku: '',
          dresseshemline: product?.show_attribute.find(item => item.label === 'Hemline')?.value,
          extralength: extralength,
          height: height,
          hip: hip,
          // hollowtoFloor: hollowtoFloor,
          // hollowtoHem: hollowtoFloor,
          length: length,
          pockets: pockets,
          back_detail: back_detail,
          product_color: '',
          product_image: productImage,
          color_price: colorPrice,
          shipingtypes: product?.show_attribute.find(item => item.label === 'Shipping Type')?.id,
          agreement: agreement ? 'no' : '',
          unit: IS_USE_CENTIMETERS_SIZE_COUNTRY ? 'cm' : 'inch',
          waist: waist,
          watermark: 1,
          select_custom_rule: selectCustomRule,
          weboSku: addToCartPayload?.weboSku,
        };

        if (product?.shipping_type === 'Dresses') {
          variables.hollowtoHem = hollowtoHem;
        } else {
          variables.hollowtoFloor = hollowtoFloor;
        }

        await addSimpleProductToCart({
          variables,
        });

        resolvePageColumn(product.categories);
      } else {
        const item = findMatchingVariant({
          optionCodes,
          optionSelections,
          variants: product.variants,
        });

        const variables: any = {
          cartId: nextCartId,
          product: {
            ...product,
            selected_options: selectedOptionsArray,
          },
          quantity,
          sku: item.product.sku,
          parentSku: product.sku,
          product_image: productImage,
          in_stock_sku: in_stock_sku,
          color_price: colorPrice,
          is_try_on: product.is_try_on,
          try_on_map_sku: product.try_on_map_sku,
          weboSku: addToCartPayload?.weboSku,
          category_id: categoryId,
          category_name: categoryName,
        };

        if (robes_gifts_customization) {
          variables.robes_gifts_customization = [`letter@@${robes_gifts_customization}`];
        }

        await addConfigurableProductToCart({
          variables,
        });
        resolvePageColumn(product.categories);
      }

      setAddToCartStatus(true);
      isSuccess = true;

      setTimeout(() => {
        setAddToCartStatus(false);
      }, 3000);
      clearCheckCustomSizeRecord?.();

      if (showMiniCart) {
        const cartAddEvent = new Event('CART_ADD_ITEM', {
          bubbles: true,
        });

        window.document.dispatchEvent(cartAddEvent);
      }

      try {
        fetch('/api/pinterest-add-to-cart', {
          method: 'POST',
          body: JSON.stringify({
            product,
            productPrice: product?.price_range?.maximum_price,
            quantity,
          }),
        });
      } catch (e) {
        console.log('Error', e);
      }
    } catch (error) {
      setAddToCartStatus(false);
      isSuccess = false;
      clearCheckCustomSizeRecord?.();
      console.error(error);
    }

    try {
      if (isSuccess) {
        afterSubmit && afterSubmit();
      }
    } catch (error) {
      console.error(error);
    }

    return isSuccess;
  };

  return {
    addToCartStatus,
    handleAddToCart,
    errorMessage:
      errorAddingConfigurableProduct ||
      errorAddingSimpleProduct ||
      (showCreateCartError
        ? new Error('Failed to create a new cart, Please refresh the page and try again')
        : undefined),
    isAdding: isAddConfigurableLoading || isAddSimpleLoading || isCreatingCart,
    selectedOptionsArray,
  };
};
